import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydInput from '../../../../../commonDesign/SydInput'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import SydCheckbox from '../../../../../commonDesign/SydCheckbox'
import SydAutocomplete from '../../../../../commonDesign/SydAutocomplete'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import { useCreateListener, useUpdateListener } from '../../../../../../api/listeners'
import { useListDags, useGetDag } from '../../../../../../api/dags'
import { decipherError } from '../../../../../../utils/decipherError'

function ListenerForm ({ existing, onCancel, onComplete }) {
  const isEdit = !!existing

  const { data: dags } = useListDags()
  const { mutateAsync: create } = useCreateListener()
  const { mutateAsync: update } = useUpdateListener()

  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)

  const dagOptions = useMemo(() =>
    (dags ?? []).map((d) => ({ value: d.dagId, label: `${d.name} (${d.dagId})` })),
  [dags])

  const initialDagOption = useMemo(() => {
    if (!existing?.dagId) return null
    return dagOptions.find((o) => o.value === existing.dagId) ?? { value: existing.dagId, label: existing.dagId }
  }, [existing, dagOptions])

  const form = useForm({
    defaultValues: isEdit && existing
      ? {
        dagId: initialDagOption,
        dagStepId: existing.dagStepId ?? '',
        eventType: existing.eventType ?? '',
        isAsync: !!existing.isAsync,
        completeOn: existing.completeOn ?? '',
        failOn: existing.failOn ?? ''
      }
      : {
        dagId: null,
        dagStepId: '',
        eventType: '',
        isAsync: false,
        completeOn: '',
        failOn: ''
      }
  })

  const watchIsAsync = form.watch('isAsync')
  const watchDagId = form.watch('dagId')
  const selectedDagId = watchDagId?.value ?? null

  const { data: selectedDag, isLoading: isDagLoading } = useGetDag(selectedDagId)

  const stepOptions = useMemo(() => {
    if (!selectedDag?.steps) return []
    return selectedDag.steps.map((s) => ({
      value: s.dagStepId,
      label: `${s.name} (${s.dagStepId})`
    }))
  }, [selectedDag])

  const handleSubmit = useCallback(async (values) => {
    const dagId = values.dagId?.value
    if (!dagId) return

    try {
      setProcessing(true)
      setError(null)

      if (isEdit) {
        await update({
          listenerId: existing.listenerId,
          eventType: values.eventType,
          dagStepId: values.dagStepId || null,
          isAsync: values.isAsync,
          completeOn: values.isAsync ? (values.completeOn || null) : null,
          failOn: values.isAsync ? (values.failOn || null) : null
        })
      } else {
        await create({
          dagId,
          dagStepId: values.dagStepId,
          eventType: values.eventType,
          isAsync: values.isAsync,
          completeOn: values.isAsync ? (values.completeOn || null) : null,
          failOn: values.isAsync ? (values.failOn || null) : null
        })
      }
      onComplete()
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [isEdit, existing, create, update, onComplete])

  const onSubmit = useCallback(async (e) => {
    const handler = form.handleSubmit(handleSubmit)
    await handler(e)
  }, [form, handleSubmit])

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            control={form.control}
            name='dagId'
            rules={{ validate: (v) => (v && v.value) ? true : 'DAG is required' }}
            render={(f) => (
              <SydLabel label='DAG' required error={hookFormErrorAdapter(form, f.fieldState)}>
                <SydAutocomplete
                  options={dagOptions}
                  value={f.field.value}
                  onChange={(_, val) => {
                    f.field.onChange(val)
                    form.setValue('dagStepId', '')
                  }}
                  placeholder='Select a DAG...'
                  size='sm'
                  disabled={isEdit}
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={form.control}
            name='dagStepId'
            rules={{ required: 'Step ID is required' }}
            render={(f) => (
              <SydLabel label='Step ID' required error={hookFormErrorAdapter(form, f.fieldState)}>
                {stepOptions.length && !isDagLoading ? (
                  <SydAutocomplete
                    options={stepOptions}
                    value={stepOptions.find((o) => o.value === f.field.value) ?? null}
                    onChange={(_, val) => f.field.onChange(val?.value ?? '')}
                    placeholder='Select a step...'
                    size='sm'
                  />
                ) : (
                  <SydInput
                    value={f.field.value}
                    onChange={f.field.onChange}
                    placeholder={!selectedDagId ? 'Select a DAG first' : isDagLoading ? 'Loading steps...' : 'No steps found — enter ID manually'}
                    disabled={!selectedDagId || isDagLoading}
                  />
                )}
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={form.control}
            name='eventType'
            rules={{ required: 'Event type is required' }}
            render={(f) => (
              <SydLabel label='Event Type' required error={hookFormErrorAdapter(form, f.fieldState)}>
                <SydInput
                  value={f.field.value}
                  onChange={f.field.onChange}
                  placeholder='e.g. com.swsapp.data.export.complete'
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={form.control}
            name='isAsync'
            render={(f) => (
              <SydLabel label='Async'>
                <SydCheckbox
                  value={f.field.value}
                  onChange={(e) => f.field.onChange(e.target.checked)}
                  label='Step uses async completion'
                />
              </SydLabel>
            )}
          />
        </Grid>
        {watchIsAsync ? (
          <>
            <Grid item xs={12} md={6}>
              <Controller
                control={form.control}
                name='completeOn'
                render={(f) => (
                  <SydLabel label='Complete On'>
                    <SydInput
                      value={f.field.value}
                      onChange={f.field.onChange}
                      placeholder='Event type for completion'
                    />
                  </SydLabel>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                control={form.control}
                name='failOn'
                render={(f) => (
                  <SydLabel label='Fail On'>
                    <SydInput
                      value={f.field.value}
                      onChange={f.field.onChange}
                      placeholder='Event type for failure'
                    />
                  </SydLabel>
                )}
              />
            </Grid>
          </>
        ) : null}
      </Grid>
      {error ? <p style={{ color: 'var(--error)', marginTop: 8 }}>{error}</p> : null}
      <SydModalActions style={{ marginTop: '24px' }}>
        <SydButton disabled={processing} variant='ghost' size='md' onClick={onCancel}>
          Cancel
        </SydButton>
        <SydButton
          processing={processing}
          variant='primary'
          size='md'
          onClick={onSubmit}
        >
          {isEdit ? 'Save Changes' : 'Create'}
        </SydButton>
      </SydModalActions>
    </>
  )
}

ListenerForm.propTypes = {
  existing: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired
}

export default ListenerForm
